import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Check, X, MapPin, Clock, MessageCircle, CalendarDays, CreditCard, Loader2 } from 'lucide-react';
import { getPackage } from '../data';
import { company, waLink } from '../mock';
import { createOrder, verifyPayment, loadRazorpay } from '../lib/api';
import { useToast } from '../hooks/use-toast';
import Header from '../components/Header';
import Footer from '../components/Footer';
import FloatingWhatsApp from '../components/FloatingWhatsApp';

export default function PackageDetail() {
  const { slug } = useParams();
  const pkg = getPackage(slug);
  const { toast } = useToast();
  const [paying, setPaying] = React.useState(false);

  const handleBook = async () => {
    if (!pkg) return;
    setPaying(true);
    try {
      const ok = await loadRazorpay();
      if (!ok) { toast({ title: 'Unable to load payment gateway', description: 'Please check your connection and try again.' }); setPaying(false); return; }
      const order = await createOrder({ amount: pkg.priceValue, package: pkg.title });
      const rzp = new window.Razorpay({
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        order_id: order.order_id,
        name: company.name,
        description: pkg.title,
        image: company.logo,
        theme: { color: '#EA5019' },
        handler: async (res) => {
          try {
            await verifyPayment(res);
            toast({ title: 'Payment successful!', description: `Booking confirmed for ${pkg.title}. Our team will contact you shortly.` });
          } catch (e) {
            toast({ title: 'Payment could not be verified', description: 'If money was deducted, please contact us with your payment ID.' });
          }
        },
        modal: { ondismiss: () => setPaying(false) },
      });
      rzp.on('payment.failed', () => { toast({ title: 'Payment failed', description: 'Please try again or use a different method.' }); setPaying(false); });
      rzp.open();
      setPaying(false);
    } catch (e) {
      toast({ title: 'Could not start payment', description: 'Please try again in a moment.' });
      setPaying(false);
    }
  };

  if (!pkg) {
    return (
      <div className="min-h-screen grid place-items-center bg-white">
        <div className="text-center">
          <p className="text-[#0B2545] text-xl font-semibold mb-4">Package not found.</p>
          <Link to="/" className="text-[#EA5019] font-semibold">Back to home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white">
      <Header />
      <section className="relative h-[56vh] min-h-[380px] flex items-end">
        <img src={pkg.img} alt={pkg.title} className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#071a33]/90 via-[#0B2545]/50 to-[#0B2545]/20" />
        <div className="relative max-w-7xl mx-auto px-5 md:px-8 w-full pb-10">
          <Link to="/#packages" className="inline-flex items-center gap-2 text-white/80 hover:text-white text-sm mb-4"><ArrowLeft size={16} /> All Packages</Link>
          <div className="flex flex-wrap gap-3 mb-3">
            <span className="px-3 py-1.5 rounded-full bg-[#EA5019] text-white text-xs font-semibold flex items-center gap-1"><Clock size={13} /> {pkg.duration}</span>
            <span className="px-3 py-1.5 rounded-full bg-white/15 backdrop-blur-sm text-white text-xs font-semibold flex items-center gap-1"><MapPin size={13} /> {pkg.places}</span>
            {pkg.packageType && <span className="px-3 py-1.5 rounded-full bg-white/15 backdrop-blur-sm text-white text-xs font-semibold">{pkg.packageType}</span>}
          </div>
          <h1 className="font-heading text-3xl md:text-5xl font-extrabold text-white">{pkg.title}</h1>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 md:px-8 py-14 grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 space-y-12">
          <div>
            <h2 className="font-heading text-2xl font-bold text-[#0B2545] mb-3">Overview</h2>
            <p className="text-slate-600 leading-relaxed">{pkg.overview}</p>
            <div className="mt-6 grid sm:grid-cols-2 gap-3">
              {pkg.highlights.map((h) => (
                <div key={h} className="flex items-start gap-2.5 text-sm text-[#0B2545]"><span className="grid place-items-center h-5 w-5 rounded-full bg-[#EA5019]/10 text-[#EA5019] shrink-0 mt-0.5"><Check size={13} /></span>{h}</div>
              ))}
            </div>
          </div>

          <div>
            <h2 className="font-heading text-2xl font-bold text-[#0B2545] mb-5">Day-by-Day Itinerary</h2>
            <div className="relative pl-6 border-l-2 border-dashed border-[#EA5019]/30 space-y-6">
              {pkg.itinerary.map((it) => (
                <div key={it.day} className="relative">
                  <span className="absolute -left-[31px] grid place-items-center h-8 w-8 rounded-full bg-[#0B2545] text-white text-xs font-bold">{it.day}</span>
                  <div className="bg-[#f6f8fb] rounded-xl p-4">
                    <div className="flex items-center gap-2 text-[#EA5019] text-xs font-semibold mb-1"><CalendarDays size={13} /> Day {it.day}</div>
                    <h3 className="font-heading font-bold text-[#0B2545]">{it.title}</h3>
                    <p className="text-sm text-slate-600 mt-1">{it.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h2 className="font-heading text-2xl font-bold text-[#0B2545] mb-5">Gallery</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {pkg.gallery.map((g, i) => (
                <div key={g} className="rounded-xl overflow-hidden aspect-[4/3] group">
                  <img src={g} alt={`${pkg.title} ${i + 1}`} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                </div>
              ))}
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-6">
            <div className="bg-[#f6f8fb] rounded-xl p-5">
              <h3 className="font-heading font-bold text-[#0B2545] mb-3">What's Included</h3>
              <ul className="space-y-2">{pkg.inclusions.map((i) => <li key={i} className="flex items-start gap-2 text-sm text-slate-600"><Check size={16} className="text-[#22a06b] shrink-0 mt-0.5" /> {i}</li>)}</ul>
            </div>
            <div className="bg-[#f6f8fb] rounded-xl p-5">
              <h3 className="font-heading font-bold text-[#0B2545] mb-3">Not Included</h3>
              <ul className="space-y-2">{pkg.exclusions.map((i) => <li key={i} className="flex items-start gap-2 text-sm text-slate-600"><X size={16} className="text-[#e04b4b] shrink-0 mt-0.5" /> {i}</li>)}</ul>
            </div>
          </div>
        </div>

        <aside className="lg:col-span-1">
          <div className="sticky top-28 bg-white rounded-2xl border border-slate-100 shadow-[0_18px_44px_rgba(11,37,69,0.12)] p-6">
            <p className="text-sm text-slate-400">Starting from</p>
            <p className="font-heading text-3xl font-extrabold text-[#0B2545]">{pkg.price}</p>
            <p className="text-xs text-slate-400 mb-5">{pkg.priceNote || "per person on twin sharing"}</p>
            <div className="space-y-3 text-sm text-[#0B2545] mb-6">
              <div className="flex items-center justify-between"><span className="text-slate-500">Duration</span><span className="font-semibold">{pkg.duration}</span></div>
              <div className="flex items-center justify-between"><span className="text-slate-500">Route</span><span className="font-semibold text-right">{pkg.places}</span></div>
            </div>
            <a href={waLink} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-2 w-full px-5 py-3.5 rounded-full bg-[#25D366] text-white font-semibold hover:brightness-95 transition mb-3"><MessageCircle size={19} /> Enquire on WhatsApp</a>
            <button onClick={handleBook} disabled={paying} className={`flex items-center justify-center gap-2 w-full px-5 py-3.5 rounded-full bg-[#EA5019] text-white font-semibold hover:bg-[#cf4413] transition mb-3 ${paying ? 'opacity-70 cursor-not-allowed' : ''}`}>
              {paying ? <><Loader2 size={19} className="animate-spin" /> Processing…</> : <><CreditCard size={19} /> Book Now &amp; Pay</>}
            </button>
            <Link to="/#contact" className="flex items-center justify-center gap-2 w-full px-5 py-3.5 rounded-full bg-[#0B2545] text-white font-semibold hover:bg-[#0B2545]/90 transition">Request a Callback</Link>
          </div>
        </aside>
      </section>
      <Footer />
      <FloatingWhatsApp />
    </div>
  );
}
